import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  LinearProgress,
  TextField,
  Toolbar,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { styled } from '@mui/system';
import { RemoveCircleOutline, Business as BusinessIcon, People as PeopleIcon, Add as AddIcon } from '@mui/icons-material';
import Selector from '../../components/common/Selector';
import AddBranchDialog from './components/addbranchdialog';
import SitemarkIcon from '../../components/common/SitemarkIcon';
import { rolesUsuarios } from '../../utils/staticvariables';

const Header = styled(AppBar)({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
  padding: '12px 40px',
});

const Step3BranchesUsers = ({ form, setForm, onBack, onNext }) => {
  const [open, setOpen] = useState(false);
  const [sucursales, setSucursales] = useState([]);
  const [errores, setErrores] = useState({});
  const [validando, setValidando] = useState(false);

  const [usuarios, setUsuarios] = useState({
    administrador: { nombre_completo: '', nombre: '', contrasena: '', sucursal: '' },
    gerente: { nombre_completo: '', nombre: '', contrasena: '', sucursal: '' },
    cajero: { nombre_completo: '', nombre: '', contrasena: '', sucursal: '' }
  });

  const [roles] = useState(rolesUsuarios);

  useEffect(() => {
    // Cargar sucursales si existen
    if (form.sucursales && form.sucursales.length > 0) {
      setSucursales(form.sucursales);
    }
  
    // Cargar usuarios si existen
    if (form.usuarios) {
      setUsuarios(form.usuarios);
    }
  }, [form]);

  const handleAddBranch = (branch) => {
    setSucursales([...sucursales, branch]);
  };

  const handleDeleteBranch = (id) => {
    setSucursales(sucursales.filter(sucursal => sucursal.id !== id));
  };

  const handleUserChange = (rol, campo, valor) => {
    setUsuarios((prev) => ({
      ...prev,
      [rol]: {
        ...prev[rol],
        [campo]: valor
      }
    }));
  };

  const verificarUsuarioExistente = async (usuario) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/usuarios/check-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ usuario })
      });

      if (response.ok) {
        const data = await response.json();
        return data.exists;
      }
      return false;
    } catch (error) {
      console.error('Error checking username:', error);
      return false;
    }
  };

  const validarUsuarios = async () => {
    const nuevosErrores = {};

    // Validación básica
    Object.entries(usuarios).forEach(([rol, datos]) => {
      if (!datos.nombre_completo) nuevosErrores[`${rol}_nombre_completo`] = 'Requerido';
      if (!datos.nombre) nuevosErrores[`${rol}_nombre`] = 'Requerido';
      if (!datos.contrasena) nuevosErrores[`${rol}_contrasena`] = 'Requerido';
      if (rol !== 'administrador' && !datos.sucursal) {
        nuevosErrores[`${rol}_sucursal`] = 'Seleccione una sucursal';
      }
    });

    setErrores(nuevosErrores);

    // Si hay errores básicos, no continuar
    if (Object.keys(nuevosErrores).length > 0) {
      return false;
    }

    // Verificar usuarios duplicados
    const usuariosExistentes = {};
    for (const [rol, datos] of Object.entries(usuarios)) {
      if (datos.nombre) {
        const existe = await verificarUsuarioExistente(datos.nombre);
        if (existe) {
          usuariosExistentes[`${rol}_nombre`] = 'Este nombre de usuario ya existe';
        }
      }
    }

    // Verificar usuarios duplicados entre los campos del formulario
    const nombresUsuarios = Object.values(usuarios).map(u => u.nombre).filter(n => n);
    const nombresDuplicados = nombresUsuarios.filter((nombre, index) => nombresUsuarios.indexOf(nombre) !== index);
    
    if (nombresDuplicados.length > 0) {
      Object.entries(usuarios).forEach(([rol, datos]) => {
        if (nombresDuplicados.includes(datos.nombre)) {
          usuariosExistentes[`${rol}_nombre`] = 'Los nombres de usuario deben ser únicos';
        }
      });
    }

    if (Object.keys(usuariosExistentes).length > 0) {
      setErrores(prev => ({ ...prev, ...usuariosExistentes }));
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    setValidando(true);
    try {
      const esValido = await validarUsuarios();
      if (!esValido) return;

      setForm({ ...form, sucursales, usuarios });
      onNext();
    } finally {
      setValidando(false);
    }
  };

  const handleBackClick = () => {
    setForm({ ...form, sucursales, usuarios });
    onBack();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #4a9b94 0%, #3a7a73 100%)',
        fontFamily: 'Inter, Noto Sans, sans-serif',
      }}
    >
      <Header position="static">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box display="flex" alignItems="center" gap={2}>
            <SitemarkIcon />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
              Bankivo Assist
            </Typography>
          </Box>
        </Toolbar>
      </Header>

      <AddBranchDialog
        addBranch={handleAddBranch}
        open={open}
        onClose={() => setOpen(false)}
      />

      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'white', mb: 1 }}>
            Sucursales y Usuarios
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Paso 3: Configura tus sucursales y usuarios iniciales
          </Typography>
        </Box>

        {/* Sucursales Section */}
        <Card sx={{ borderRadius: 2, boxShadow: 4, mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Crear Sucursales
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Agrega al menos una sucursal para tu empresa
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpen(true)}
                  sx={{
                    backgroundColor: '#18586b',
                    fontWeight: 600,
                    borderRadius: 1,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#0f4c75',
                    },
                  }}
                >
                  Agregar Sucursal
                </Button>
              </Box>
            </Box>

            {sucursales.length > 0 ? (
              <TableContainer component={Paper} sx={{ borderRadius: 1, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Nombre de Sucursal</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Dirección Física</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Teléfono y Contacto</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sucursales.map((sucursal) => (
                      <TableRow key={sucursal.id}>
                        <TableCell>{sucursal.nombre}</TableCell>
                        <TableCell>{sucursal.direccion}</TableCell>
                        <TableCell>{sucursal.contacto}</TableCell>
                        <TableCell>
                          <IconButton 
                            onClick={() => handleDeleteBranch(sucursal.id)}
                            sx={{ color: 'error.main', '&:hover': { backgroundColor: 'error.light', opacity: 0.8 } }}
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  No hay sucursales agregadas. Haz clic en "Agregar Sucursal" para comenzar.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Usuarios Section */}
        <Card sx={{ borderRadius: 2, boxShadow: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PeopleIcon sx={{ fontSize: 28, color: 'primary.main' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Crear Usuarios Iniciales
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configura los usuarios iniciales para cada rol
                </Typography>
              </Box>
            </Box>

            <Box display="flex" flexDirection="row" alignItems="flex-start" gap={4} flexWrap="wrap">
              {roles.map((rolTexto) => {
                const rolKey = rolTexto.name.toLowerCase().includes("administrador") ? "administrador" : rolTexto.name.toLowerCase();
                return (
                  <Card key={rolTexto.name} sx={{ flex: '1 1 300px', minWidth: 300, borderRadius: 2, boxShadow: 2 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                        {rolTexto.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Los permisos se pueden personalizar más adelante
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                            Nombre Completo *
                          </Typography>
                          <TextField
                            placeholder="Nombre completo"
                            value={usuarios[rolKey].nombre_completo}
                            onChange={(e) => handleUserChange(rolKey, 'nombre_completo', e.target.value)}
                            error={!!errores[`${rolKey}_nombre_completo`]}
                            helperText={errores[`${rolKey}_nombre_completo`]}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F8F9FA',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                            Nombre de Usuario *
                          </Typography>
                          <TextField
                            placeholder="Nombre de usuario"
                            value={usuarios[rolKey].nombre}
                            onChange={(e) => handleUserChange(rolKey, 'nombre', e.target.value)}
                            error={!!errores[`${rolKey}_nombre`]}
                            helperText={errores[`${rolKey}_nombre`]}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F8F9FA',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                            Contraseña *
                          </Typography>
                          <TextField
                            placeholder="Contraseña"
                            type="password"
                            value={usuarios[rolKey].contrasena}
                            onChange={(e) => handleUserChange(rolKey, 'contrasena', e.target.value)}
                            error={!!errores[`${rolKey}_contrasena`]}
                            helperText={errores[`${rolKey}_contrasena`]}
                            fullWidth
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: '#F8F9FA',
                                borderRadius: 1,
                              },
                            }}
                          />
                        </Box>
                        {rolKey !== "administrador" && (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                              Sucursal *
                            </Typography>
                            <Selector
                              value={usuarios[rolKey].sucursal}
                              onChange={(e) => handleUserChange(rolKey, 'sucursal', e.target.value)}
                              options={sucursales.map(s => ({ value: s.id, label: s.nombre }))}
                              placeholder="Seleccione sucursal"
                              error={!!errores[`${rolKey}_sucursal`]}
                              sx={{
                                backgroundColor: '#F8F9FA',
                                borderRadius: 1,
                              }}
                            />
                            {errores[`${rolKey}_sucursal`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', px: 1 }}>
                                {errores[`${rolKey}_sucursal`]}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </CardContent>
        </Card>

        <Box mt={4}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
            Paso 3 de 3
          </Typography>
          <LinearProgress
            variant="determinate"
            value={100}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.3)',
              height: 8,
              borderRadius: 1,
              '& .MuiLinearProgress-bar': {
                bgcolor: 'white',
              },
            }}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Button
            variant="outlined"
            onClick={handleBackClick}
            disabled={validando}
            sx={{
              fontWeight: 600,
              borderRadius: 1,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              borderColor: 'white',
              color: 'white',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            Anterior
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={validando}
            sx={{
              fontWeight: 600,
              borderRadius: 1,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              backgroundColor: 'white',
              color: '#18586b',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
            }}
          >
            {validando ? 'Validando...' : 'Continuar'}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Step3BranchesUsers;

